import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from "react";
import { Session, User, AuthChangeEvent } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { SUPABASE_PROJECT_ID, TOKEN_REFRESH_WINDOW_MS } from "@/lib/constants";

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  /** True when we know auth is ready (even if no session) */
  initialized: boolean;
  /** True when localStorage is unavailable (private browsing) */
  storageUnavailable: boolean;
  signOut: () => Promise<void>;
  /** Get a valid access token, or null if not authenticated. Handles refresh. */
  getAccessToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  loading: true,
  initialized: false,
  storageUnavailable: false,
  signOut: async () => {},
  getAccessToken: async () => null,
});

/** Test if localStorage is available (fails in some private browsing modes) */
function isStorageAvailable(): boolean {
  try {
    const testKey = "__storage_test__";
    localStorage.setItem(testKey, "1");
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

/**
 * Validate that stored session JSON is not corrupted.
 * FIX #5: Only clears keys matching THIS project's Supabase project ID,
 * not all sb-* keys (which could belong to other Supabase projects on the same domain).
 */
function validateStoredSession(): boolean {
  try {
    const key = `sb-${SUPABASE_PROJECT_ID}-auth-token`;
    const raw = localStorage.getItem(key);
    if (!raw) return true; // No session is valid state
    JSON.parse(raw); // Will throw on corrupted JSON
    return true;
  } catch {
    // Corrupted session — clear only THIS project's token
    try {
      localStorage.removeItem(`sb-${SUPABASE_PROJECT_ID}-auth-token`);
    } catch { /* ignore */ }
    return false;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [storageUnavailable] = useState(() => !isStorageAvailable());

  // FIX #5: Flag to prevent in-flight token refresh from writing back a stale session after logout
  const isSigningOutRef = useRef(false);
  const refreshPromiseRef = useRef<Promise<string | null> | null>(null);

  // Warn once in dev if private browsing detected — session won't persist
  if (storageUnavailable && import.meta.env.DEV) {
    console.warn("[Auth] localStorage unavailable (private browsing?). Session will not persist.");
  }

  useEffect(() => {
    // Validate stored session on mount
    if (!storageUnavailable) {
      validateStoredSession();
    }

    // Set up auth state listener FIRST (before getSession)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event: AuthChangeEvent, newSession: Session | null) => {
        // FIX #5: If we're in the middle of signing out, don't let a stale
        // TOKEN_REFRESHED event write back a session
        if (isSigningOutRef.current && event === "TOKEN_REFRESHED") return;

        setSession(newSession);
        setLoading(false);
        setInitialized(true);

        // Handle cross-tab sign out
        if (event === "SIGNED_OUT") {
          setSession(null);
        }

        // Handle token refresh failure
        if (event === "TOKEN_REFRESHED" && !newSession) {
          toast.error("Session expired. Please sign in again.");
        }

        // Handle user deleted server-side (token refresh will fail)
        if (event === "SIGNED_OUT" && !newSession) {
          // Clear any stale state
          setSession(null);
        }
      }
    );

    // Then get initial session — always resolves (even in private browsing)
    // FIX #21: Timeout so a hung Supabase connection doesn't block app initialization
    const sessionTimeout = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error("getSession timed out")), 10_000)
    );
    Promise.race([supabase.auth.getSession(), sessionTimeout]).then(({ data: { session: initialSession }, error }) => {
      if (error) {
        console.error("Failed to get session:", error);
        // Session corrupted or unavailable — treat as signed out, never block the app
        setSession(null);
      } else {
        setSession(initialSession ?? null);
      }
      setLoading(false);
      setInitialized(true);
    }).catch(() => {
      // Absolute fallback — storage unavailable, timeout, or network failure
      setSession(null);
      setLoading(false);
      setInitialized(true);
    });

    // Listen for storage events (cross-tab sign out detection)
    // Only fire if the key was removed AND this tab has an active session
    // (avoids false positives from token refresh writing new values)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key && e.key.startsWith("sb-") && e.key.includes("auth-token")) {
        if (!e.newValue && e.oldValue) {
          // Token was explicitly removed in another tab (not just a refresh) — sign out here too
          setSession(null);
          toast.info("Signed out from another tab");
        }
      }
    };
    window.addEventListener("storage", handleStorageChange);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [storageUnavailable]);

  const signOut = useCallback(async () => {
    isSigningOutRef.current = true;
    // Immediately clear session so UI reflects signed-out state
    setSession(null);
    // Cancel any in-flight refresh so it doesn't write back a stale session
    refreshPromiseRef.current = null;
    try {
      await supabase.auth.signOut();
    } catch (err) {
      console.error("Sign out error:", err);
    } finally {
      isSigningOutRef.current = false;
    }
  }, []);

  /**
   * Get a valid access token. Handles:
   * - Token refresh race conditions — uses a singleton promise to deduplicate concurrent calls (FIX #4)
   * - Returns null if not authenticated (instead of falling back to anon key)
   */
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    try {
      const { data: { session: currentSession }, error } = await supabase.auth.getSession();
      if (error || !currentSession) return null;

      // Check if token is about to expire (within the refresh window)
      const expiresAt = currentSession.expires_at;
      if (expiresAt && expiresAt * 1000 - Date.now() < TOKEN_REFRESH_WINDOW_MS) {
        // FIX #4: deduplicate concurrent refresh calls with a singleton promise
        if (!refreshPromiseRef.current) {
          refreshPromiseRef.current = supabase.auth.refreshSession()
            .then(({ data: { session: refreshed }, error: refreshError }) => {
              // FIX #5: If signOut was called during refresh, discard the result
              if (isSigningOutRef.current || refreshError || !refreshed) return null;
              return refreshed.access_token;
            })
            .finally(() => { refreshPromiseRef.current = null; });
        }
        return refreshPromiseRef.current;
      }

      return currentSession.access_token;
    } catch {
      return null;
    }
  }, []);

  return (
    <AuthContext.Provider value={{
      session,
      user: session?.user ?? null,
      loading,
      initialized,
      storageUnavailable,
      signOut,
      getAccessToken,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
